import React, { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiErr } from "@/lib/api";
import { rupiah, rupiahShort } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import NumberInput from "@/components/ui/number-input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, ChevronLeft, ChevronRight, Save, RotateCcw, TrendingUp, TrendingDown, Lock, Unlock } from "lucide-react";

const MONTHS_ID = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];

function pctColor(pct) {
  if (pct >= 100) return "#10B981"; // green — hit or exceeded target
  if (pct >= 70) return "#F59E0B"; // amber — close
  return "#EF4444"; // red — well below target
}

function AchievementCard({ label, target, actual, gap, achievement_pct }) {
  const color = pctColor(achievement_pct);
  return (
    <div className="card-surface rounded-2xl p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="font-semibold text-sm">{label}</span>
        <span className="text-lg font-bold font-heading" style={{ color }}>{achievement_pct}%</span>
      </div>
      <div className="h-1.5 rounded-full bg-[#18181B] overflow-hidden mb-3">
        <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(100, achievement_pct)}%`, background: color }} />
      </div>
      <div className="grid grid-cols-3 gap-2 text-xs">
        <div><div className="text-zinc-500">Target</div><div className="font-medium">{rupiahShort(target)}</div></div>
        <div><div className="text-zinc-500">Actual</div><div className="font-medium text-cyan-400">{rupiahShort(actual)}</div></div>
        <div>
          <div className="text-zinc-500">Gap</div>
          <div className="font-medium flex items-center gap-1" style={{ color: gap >= 0 ? "#10B981" : "#EF4444" }}>
            {gap >= 0 ? <TrendingUp size={11} /> : <TrendingDown size={11} />}{rupiahShort(Math.abs(gap))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function CompanyForecast({ canEdit }) {
  const qc = useQueryClient();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [level, setLevel] = useState("monthly"); // monthly | quarterly | yearly
  const [annualInput, setAnnualInput] = useState("");
  const [monthlyEdit, setMonthlyEdit] = useState(null); // null = not editing; else {1: val, ...}
  const [saving, setSaving] = useState(false);
  // Locked by default every time this page loads — prevents an accidental tap/click on
  // the annual target field (which drives every monthly target company-wide) from
  // silently overwriting it. Owner/Chief must deliberately unlock before editing.
  const [locked, setLocked] = useState(true);

  const { data, isLoading } = useQuery({
    queryKey: ["company-target", year],
    queryFn: async () => (await api.get(`/company-targets/${year}`)).data,
  });

  useEffect(() => {
    if (data) setAnnualInput(String(data.annual_target || ""));
  }, [data]);

  const saveAnnual = async () => {
    const val = Math.max(0, Number(annualInput) || 0);
    setSaving(true);
    try {
      await api.put(`/company-targets/${year}`, { annual_target: val });
      toast.success("Target tahunan disimpan — dibagi rata 12 bulan");
      qc.invalidateQueries({ queryKey: ["company-target", year] });
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  const startEditMonthly = () => {
    const m = {};
    (data?.monthly || []).forEach((row) => { m[row.month] = row.target; });
    setMonthlyEdit(m);
  };

  const saveMonthly = async () => {
    setSaving(true);
    try {
      await api.put(`/company-targets/${year}`, { monthly: monthlyEdit });
      toast.success("Target bulanan disimpan");
      qc.invalidateQueries({ queryKey: ["company-target", year] });
      setMonthlyEdit(null);
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  if (isLoading || !data) return null;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <button data-testid="fc-year-prev" onClick={() => setYear((y) => y - 1)} className="w-8 h-8 rounded-lg flex items-center justify-center text-zinc-400 hover:text-white hover:bg-white/5"><ChevronLeft size={16} /></button>
          <span className="font-heading font-bold text-lg min-w-[70px] text-center">{year}</span>
          <button data-testid="fc-year-next" onClick={() => setYear((y) => y + 1)} className="w-8 h-8 rounded-lg flex items-center justify-center text-zinc-400 hover:text-white hover:bg-white/5"><ChevronRight size={16} /></button>
        </div>
        <div className="flex bg-[#18181B] rounded-lg p-1 border border-white/5">
          {[["monthly", "Bulanan"], ["quarterly", "Kuartalan"], ["yearly", "Tahunan"]].map(([v, l]) => (
            <button key={v} data-testid={`fc-level-${v}`} onClick={() => setLevel(v)} className={`px-2.5 py-1.5 rounded-md text-xs font-medium ${level === v ? "bg-blue-500/20 text-blue-300" : "text-zinc-500"}`}>{l}</button>
          ))}
        </div>
      </div>

      {!canEdit && (
        <div className="rounded-xl bg-blue-500/10 border border-blue-500/20 px-3 py-2 text-xs text-blue-300">
          Mode lihat saja — hanya Owner/Chief yang bisa mengatur Target Tahunan Perusahaan.
        </div>
      )}

      {canEdit && (
        <div className="card-surface rounded-2xl p-4">
          <div className="flex items-center justify-between">
            <Label className="text-xs text-zinc-400 uppercase">Target Tahunan {year} (Rp)</Label>
            <button data-testid="fc-lock-toggle" onClick={() => setLocked((v) => !v)}
              className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-lg border ${locked ? "bg-[#18181B] border-white/10 text-zinc-400" : "bg-amber-500/15 border-amber-500/30 text-amber-400"}`}>
              {locked ? <><Lock size={12} />Terkunci</> : <><Unlock size={12} />Terbuka — bisa diubah</>}
            </button>
          </div>
          <div className="flex gap-2 mt-1.5">
            <NumberInput data-testid="fc-annual-input" disabled={locked} value={annualInput} onChange={(e) => setAnnualInput(e.target.value)}
              placeholder="mis. 12000000000" className="bg-[#18181B] border-white/10 h-10 disabled:opacity-50" />
            <Button data-testid="fc-annual-save" onClick={saveAnnual} disabled={saving || locked} className="h-10 shrink-0 bg-gradient-to-r from-blue-700 to-blue-900">
              {saving ? <Loader2 size={16} className="animate-spin" /> : <><Save size={15} className="mr-1.5" />Simpan</>}
            </Button>
          </div>
          <p className="text-[11px] text-zinc-500 mt-1.5">{locked ? "Klik \"Terkunci\" di atas untuk buka kunci sebelum mengubah — mencegah perubahan tidak sengaja." : "Otomatis dibagi rata 12 bulan. Butuh target musiman (tidak rata)? Edit manual per bulan di bawah."}</p>
        </div>
      )}

      {level === "yearly" && (
        <AchievementCard label={`Tahun ${year}`} {...data.yearly} />
      )}

      {level === "quarterly" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {data.quarterly.map((q) => (
            <AchievementCard key={q.quarter} label={`Q${q.quarter} ${year}`} {...q} />
          ))}
        </div>
      )}

      {level === "monthly" && (
        <div className="space-y-3">
          {canEdit && (
            <div className="flex justify-end">
              {monthlyEdit === null ? (
                <Button data-testid="fc-monthly-edit" onClick={startEditMonthly} disabled={locked} variant="outline" size="sm" className="h-8 border-white/10 text-xs disabled:opacity-40">
                  <RotateCcw size={13} className="mr-1.5" />Edit Manual per Bulan
                </Button>
              ) : (
                <div className="flex gap-2">
                  <Button data-testid="fc-monthly-cancel" onClick={() => setMonthlyEdit(null)} variant="outline" size="sm" className="h-8 border-white/10 text-xs">Batal</Button>
                  <Button data-testid="fc-monthly-save" onClick={saveMonthly} disabled={saving} size="sm" className="h-8 bg-gradient-to-r from-blue-700 to-blue-900 text-xs">
                    {saving ? <Loader2 size={13} className="animate-spin" /> : "Simpan Semua Bulan"}
                  </Button>
                </div>
              )}
            </div>
          )}
          {monthlyEdit !== null ? (
            <div className="card-surface rounded-2xl p-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
              {data.monthly.map((row) => (
                <div key={row.month}>
                  <Label className="text-[10px] text-zinc-400 uppercase">{MONTHS_ID[row.month - 1]}</Label>
                  <NumberInput data-testid={`fc-month-input-${row.month}`} value={monthlyEdit[row.month]}
                    onChange={(e) => setMonthlyEdit((m) => ({ ...m, [row.month]: Number(e.target.value) || 0 }))}
                    className="bg-[#18181B] border-white/10 h-9 mt-1 text-sm" />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {data.monthly.map((row) => (
                <AchievementCard key={row.month} label={`${MONTHS_ID[row.month - 1]} ${year}`} {...row} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
